import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/ContextoAuth'
import { autenticacion, caja as apiCaja, ventas as apiVentas } from '../api/servicios'
import { fechaHora, hora, dinero } from '../api/formato'
import type {
    Caja as CajaRegistro,
    DesgloseEfectivo,
    MovimientoCaja,
    SesionCaja,
    TipoMovimientoCaja,
} from '../api/tipos'
import { Dialogo } from '../componentes/Dialogo'
import { Estado } from '../componentes/Estado'
import { mensajeDeError, usarCargar } from '../componentes/usarCargar'
import './caja.css'

/**
 * Pantalla de caja: apertura, movimientos manuales y cierre ciego.
 *
 * El POS depende de esta pantalla, porque sin sesion abierta no se puede
 * vender. Por eso la apertura es lo primero que se ve cuando no hay sesion, sin
 * pasos intermedios.
 */
export function Caja() {
    // Al cerrar, el backend deja de reportar sesion activa. Se conserva aqui la
    // sesion recien cerrada para poder mostrar el reporte de cierre, que es lo
    // que el cajero necesita imprimir antes de irse.
    const [cerrada, setCerrada] = useState<SesionCaja | null>(null)
    const estado = usarCargar(() => apiCaja.sesionActiva(), [])

    if (cerrada) {
        return (
            <ReporteDeCierre
                sesion={cerrada}
                alTerminar={() => {
                    setCerrada(null)
                    estado.recargar()
                }}
            />
        )
    }

    return (
        <div className="pagina-caja columna g4">
            <Estado cargando={estado.cargando} error={estado.error} alReintentar={estado.recargar}>
                {estado.datos ? (
                    <SesionAbierta
                        sesion={estado.datos}
                        alActualizar={estado.fijar}
                        alCerrarse={setCerrada}
                    />
                ) : (
                    <Apertura alAbrir={estado.fijar} />
                )}
            </Estado>
        </div>
    )
}

/* -------------------------------------------------------------------------- */
/* Apertura                                                                    */
/* -------------------------------------------------------------------------- */

function Apertura({ alAbrir }: { alAbrir: (sesion: SesionCaja) => void }) {
    const cajas = usarCargar(() => apiCaja.listarCajas(), [])
    const [cajaId, setCajaId] = useState('')
    const [monto, setMonto] = useState('')
    const [observacion, setObservacion] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    const disponibles = (cajas.datos ?? []).filter((c) => c.activa && c.sesionAbiertaId === null)
    const ocupadas = (cajas.datos ?? []).filter((c) => c.activa && c.sesionAbiertaId !== null)

    // Con una sola caja disponible —el caso normal en una tienda chica— se elige
    // sola: un desplegable de un solo elemento es un clic de mas.
    const unica = disponibles.length === 1 ? disponibles[0]!.cajaId : null

    useEffect(() => {
        if (unica !== null) setCajaId((actual) => actual || String(unica))
    }, [unica])

    async function abrir(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (!cajaId) {
            setError('Elija la caja que va a abrir.')
            return
        }

        const importe = Number(monto)

        if (monto.trim() === '' || !Number.isFinite(importe) || importe < 0) {
            setError('Ingrese el efectivo con el que parte la caja. Si parte vacía, escriba 0.')
            return
        }

        setEnviando(true)

        try {
            const sesion = await apiCaja.abrir(
                Number(cajaId),
                Math.round(importe),
                observacion.trim() || undefined,
            )

            alAbrir(sesion)
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <section className="panel columna g4 tarjeta-apertura">
            <header className="columna g1">
                <h1>Abrir caja</h1>
                <p className="tenue chico">
                    No hay una sesión de caja abierta a su nombre. Sin ella no se puede vender.
                </p>
            </header>

            <Estado
                cargando={cajas.cargando}
                error={cajas.error}
                alReintentar={cajas.recargar}
                vacio={disponibles.length === 0}
                mensajeVacio={
                    ocupadas.length > 0
                        ? 'Todas las cajas están ocupadas. Debe cerrarse la sesión anterior antes de abrir una nueva.'
                        : 'No hay cajas activas configuradas.'
                }
            >
                <form className="columna g4" onSubmit={abrir}>
                    <div>
                        <label className="etiqueta" htmlFor="caja">
                            Caja
                        </label>
                        <select
                            id="caja"
                            className="campo"
                            value={cajaId}
                            onChange={(e) => setCajaId(e.target.value)}
                        >
                            <option value="">Elija una caja…</option>
                            {disponibles.map((c: CajaRegistro) => (
                                <option key={c.cajaId} value={c.cajaId}>
                                    {c.nombre} ({c.codigo})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="etiqueta" htmlFor="monto">
                            Efectivo inicial
                        </label>
                        <input
                            id="monto"
                            className="campo campo-numero"
                            type="number"
                            min={0}
                            step={1}
                            inputMode="numeric"
                            placeholder="0"
                            value={monto}
                            onChange={(e) => setMonto(e.target.value)}
                        />
                        <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                            Cuente el fondo con el que parte el turno. Este monto entra en el
                            arqueo del cierre.
                        </p>
                    </div>

                    <div>
                        <label className="etiqueta" htmlFor="observacion">
                            Observación <span className="tenue">(opcional)</span>
                        </label>
                        <input
                            id="observacion"
                            className="campo"
                            maxLength={500}
                            value={observacion}
                            onChange={(e) => setObservacion(e.target.value)}
                        />
                    </div>

                    {error && (
                        <div className="aviso aviso-error" role="alert">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="boton boton-primario boton-grande boton-bloque"
                        disabled={enviando}
                    >
                        {enviando ? 'Abriendo…' : 'Abrir caja'}
                    </button>
                </form>
            </Estado>

            {ocupadas.length > 0 && (
                <p className="tenue diminuto">
                    Ocupadas: {ocupadas.map((c) => c.nombre).join(', ')}.
                </p>
            )}
        </section>
    )
}

/* -------------------------------------------------------------------------- */
/* Sesion abierta                                                              */
/* -------------------------------------------------------------------------- */

interface PropsSesion {
    sesion: SesionCaja
    alActualizar: (sesion: SesionCaja) => void
    alCerrarse: (sesion: SesionCaja) => void
}

function SesionAbierta({ sesion, alActualizar, alCerrarse }: PropsSesion) {
    const { puede } = useAuth()
    const puedeMover = puede('caja.movimiento')
    const puedeCerrar = puede('caja.cerrar')

    const movimientos = usarCargar<MovimientoCaja[]>(
        () => (puedeMover ? apiCaja.movimientos(sesion.sesionCajaId) : Promise.resolve([])),
        [sesion.sesionCajaId, puedeMover],
    )

    const [dialogo, setDialogo] = useState<'ninguno' | 'movimiento' | 'cierre'>('ninguno')

    return (
        <>
            <section className="panel columna g4">
                <header className="fila entre envuelve g3">
                    <div className="columna g1">
                        <h1>{sesion.cajaNombre}</h1>
                        <p className="tenue chico">
                            Abierta el {fechaHora(sesion.abiertaEn)}
                        </p>
                    </div>

                    <span className="insignia insignia-ok">Abierta</span>
                </header>

                <div className="rejilla-datos">
                    <Dato titulo="Efectivo inicial" valor={dinero(sesion.montoInicial)} />
                    <Dato
                        titulo="Conteo"
                        valor={
                            sesion.conteoRegistradoEn
                                ? dinero(sesion.efectivoContado)
                                : 'Sin registrar'
                        }
                        tenue={!sesion.conteoRegistradoEn}
                    />
                    <Dato
                        titulo="Esperado"
                        valor={sesion.esperadoOculto ? 'Oculto' : dinero(sesion.efectivoEsperado)}
                        tenue={sesion.esperadoOculto}
                    />
                </div>

                {sesion.esperadoOculto && (
                    <p className="tenue diminuto">
                        El cierre es ciego: el efectivo esperado se muestra recién después de
                        registrar el conteo físico.
                    </p>
                )}

                <div className="fila envuelve g3 acciones-caja">
                    <Link className="boton boton-primario" to="/pos">
                        Ir al POS
                    </Link>

                    {puedeMover && (
                        <button
                            type="button"
                            className="boton"
                            onClick={() => setDialogo('movimiento')}
                        >
                            Registrar movimiento
                        </button>
                    )}

                    {puedeCerrar && (
                        <button
                            type="button"
                            className="boton boton-peligro"
                            onClick={() => setDialogo('cierre')}
                        >
                            Cerrar caja
                        </button>
                    )}
                </div>
            </section>

            {puedeMover && (
                <section className="panel columna g3">
                    <h2>Movimientos del turno</h2>

                    <Estado
                        cargando={movimientos.cargando}
                        error={movimientos.error}
                        alReintentar={movimientos.recargar}
                        vacio={(movimientos.datos ?? []).length === 0}
                        mensajeVacio="Sin ingresos, egresos ni retiros en este turno."
                    >
                        <TablaMovimientos movimientos={movimientos.datos ?? []} />
                    </Estado>
                </section>
            )}

            {dialogo === 'movimiento' && (
                <DialogoMovimiento
                    sesionCajaId={sesion.sesionCajaId}
                    alRegistrado={() => {
                        setDialogo('ninguno')
                        movimientos.recargar()
                    }}
                    alCerrar={() => setDialogo('ninguno')}
                />
            )}

            {dialogo === 'cierre' && (
                <DialogoCierre
                    sesion={sesion}
                    alActualizar={alActualizar}
                    alCerrada={(cerrada) => {
                        setDialogo('ninguno')
                        alCerrarse(cerrada)
                    }}
                    alCerrar={() => setDialogo('ninguno')}
                />
            )}
        </>
    )
}

function Dato({ titulo, valor, tenue }: { titulo: string; valor: string; tenue?: boolean }) {
    return (
        <div className="dato">
            <span className="etiqueta">{titulo}</span>
            <strong className={`numero${tenue ? ' tenue' : ''}`}>{valor}</strong>
        </div>
    )
}

/** Un ingreso suma al efectivo; egresos y retiros lo restan. */
function signoDe(tipo: TipoMovimientoCaja): number {
    return tipo === 'Ingreso' ? 1 : -1
}

function TablaMovimientos({ movimientos }: { movimientos: MovimientoCaja[] }) {
    return (
        <div className="tabla-envoltura">
            <table className="tabla">
                <thead>
                    <tr>
                        <th>Hora</th>
                        <th>Tipo</th>
                        <th>Motivo</th>
                        <th className="derecha">Importe</th>
                    </tr>
                </thead>
                <tbody>
                    {movimientos.map((m) => (
                        <tr key={m.movimientoCajaId}>
                            <td className="numero">{hora(m.registradoEn)}</td>
                            <td>{m.tipo}</td>
                            <td>
                                {m.motivo}
                                {m.referencia && (
                                    <span className="tenue diminuto"> · {m.referencia}</span>
                                )}
                            </td>
                            <td
                                className={`derecha numero ${
                                    signoDe(m.tipo) > 0 ? 'monto-suma' : 'monto-resta'
                                }`}
                            >
                                {signoDe(m.tipo) > 0 ? '+' : '−'}
                                {dinero(m.importe)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

/* -------------------------------------------------------------------------- */
/* Movimiento manual                                                           */
/* -------------------------------------------------------------------------- */

const TIPOS: { valor: TipoMovimientoCaja; texto: string; ayuda: string }[] = [
    { valor: 'Ingreso', texto: 'Ingreso', ayuda: 'Entra efectivo a la caja.' },
    { valor: 'Egreso', texto: 'Egreso', ayuda: 'Sale efectivo por un gasto.' },
    { valor: 'Retiro', texto: 'Retiro', ayuda: 'Sale efectivo hacia la caja fuerte o el banco.' },
]

interface PropsMovimiento {
    sesionCajaId: number
    alRegistrado: () => void
    alCerrar: () => void
}

function DialogoMovimiento({ sesionCajaId, alRegistrado, alCerrar }: PropsMovimiento) {
    const autorizadores = usarCargar(
        () => autenticacion.autorizadores('caja.movimiento.autorizar'),
        [],
    )

    const [tipo, setTipo] = useState<TipoMovimientoCaja>('Ingreso')
    const [importe, setImporte] = useState('')
    const [motivo, setMotivo] = useState('')
    const [referencia, setReferencia] = useState('')
    const [autorizador, setAutorizador] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    async function registrar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        const valor = Number(importe)

        if (!Number.isFinite(valor) || valor <= 0) {
            setError('Ingrese un importe mayor que cero.')
            return
        }

        if (motivo.trim().length === 0) {
            setError('El motivo es obligatorio: un movimiento sin explicación es un descuadre.')
            return
        }

        if (!autorizador) {
            setError('Elija quién autoriza el movimiento.')
            return
        }

        setEnviando(true)

        try {
            await apiCaja.registrarMovimiento(sesionCajaId, {
                tipo,
                importe: Math.round(valor),
                motivo: motivo.trim(),
                referencia: referencia.trim() || undefined,
                autorizadoPorUsuarioId: Number(autorizador),
            })

            alRegistrado()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo="Movimiento de caja"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-movimiento"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Registrando…' : 'Registrar'}
                    </button>
                </>
            }
        >
            <form id="forma-movimiento" className="columna g4" onSubmit={registrar}>
                <div>
                    <span className="etiqueta">Tipo</span>
                    <div className="grupo-opciones">
                        {TIPOS.map((opcion) => (
                            <button
                                key={opcion.valor}
                                type="button"
                                className={`boton${tipo === opcion.valor ? ' boton-primario' : ''}`}
                                aria-pressed={tipo === opcion.valor}
                                onClick={() => setTipo(opcion.valor)}
                            >
                                {opcion.texto}
                            </button>
                        ))}
                    </div>
                    <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                        {TIPOS.find((o) => o.valor === tipo)?.ayuda}
                    </p>
                </div>

                <div>
                    <label className="etiqueta" htmlFor="importe">
                        Importe
                    </label>
                    <input
                        id="importe"
                        className="campo campo-numero"
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        value={importe}
                        onChange={(e) => setImporte(e.target.value)}
                    />
                </div>

                <div>
                    <label className="etiqueta" htmlFor="motivo">
                        Motivo
                    </label>
                    <input
                        id="motivo"
                        className="campo"
                        maxLength={500}
                        placeholder="Compra de bolsas, retiro a caja fuerte…"
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                    />
                </div>

                <div>
                    <label className="etiqueta" htmlFor="referencia">
                        Referencia <span className="tenue">(opcional)</span>
                    </label>
                    <input
                        id="referencia"
                        className="campo"
                        maxLength={80}
                        placeholder="N° de boleta o comprobante"
                        value={referencia}
                        onChange={(e) => setReferencia(e.target.value)}
                    />
                </div>

                <div>
                    <label className="etiqueta" htmlFor="autorizador">
                        Autoriza
                    </label>

                    <Estado
                        cargando={autorizadores.cargando}
                        error={autorizadores.error}
                        alReintentar={autorizadores.recargar}
                        vacio={(autorizadores.datos ?? []).length === 0}
                        mensajeVacio="No hay ningún usuario con permiso para autorizar movimientos."
                    >
                        <select
                            id="autorizador"
                            className="campo"
                            value={autorizador}
                            onChange={(e) => setAutorizador(e.target.value)}
                        >
                            <option value="">Elija quién autoriza…</option>
                            {(autorizadores.datos ?? []).map((a) => (
                                <option key={a.usuarioId} value={a.usuarioId}>
                                    {a.nombreCompleto}
                                </option>
                            ))}
                        </select>
                    </Estado>

                    <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                        Debe ser el supervisor que está presente. Queda registrado en la
                        auditoría junto a su nombre.
                    </p>
                </div>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */
/* Cierre ciego                                                                */
/* -------------------------------------------------------------------------- */

interface PropsCierre {
    sesion: SesionCaja
    alActualizar: (sesion: SesionCaja) => void
    alCerrada: (sesion: SesionCaja) => void
    alCerrar: () => void
}

function DialogoCierre({ sesion, alActualizar, alCerrada, alCerrar }: PropsCierre) {
    // Comparacion holgada a proposito: si el campo llegara ausente en vez de
    // nulo, '!== null' lo daria por contado y el cajero se saltaria el conteo
    // entero, que es el paso que hace ciego al cierre.
    const conteoHecho = sesion.conteoRegistradoEn != null

    return conteoHecho ? (
        <PasoCierre sesion={sesion} alCerrada={alCerrada} alCerrar={alCerrar} />
    ) : (
        <PasoConteo sesion={sesion} alActualizar={alActualizar} alCerrar={alCerrar} />
    )
}

function PasoConteo({
    sesion,
    alActualizar,
    alCerrar,
}: {
    sesion: SesionCaja
    alActualizar: (sesion: SesionCaja) => void
    alCerrar: () => void
}) {
    const [contado, setContado] = useState('')

    // Los medios que no son efectivo: lo que el cajero copia del cierre que
    // imprime el lector de tarjetas.
    const medios = usarCargar(() => apiVentas.mediosPago(), [])
    const conTarjeta = (medios.datos ?? []).filter((m) => !m.afectaEfectivo)
    const [declarados, setDeclarados] = useState<Record<number, string>>({})

    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    async function registrar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        const valor = Number(contado)

        if (contado.trim() === '' || !Number.isFinite(valor) || valor < 0) {
            setError('Ingrese el efectivo contado. Si la caja quedó vacía, escriba 0.')
            return
        }

        setEnviando(true)

        try {
            // Solo se envian los medios que el cajero completo: dejar uno en
            // blanco significa "no lo declaro", que no es lo mismo que cero.
            const totales = conTarjeta
                .filter((m) => (declarados[m.medioPagoId] ?? '').trim() !== '')
                .map((m) => ({
                    medioPagoId: m.medioPagoId,
                    importe: Math.round(Number(declarados[m.medioPagoId])),
                }))

            if (totales.some((x) => !Number.isFinite(x.importe) || x.importe < 0)) {
                setError('Los totales del lector tienen que ser números y no pueden ser negativos.')
                setEnviando(false)
                return
            }

            // El conteo se registra una sola vez: el backend rechaza el segundo
            // intento, que es justamente lo que hace ciego al arqueo.
            alActualizar(
                await apiCaja.registrarConteo(sesion.sesionCajaId, Math.round(valor), totales),
            )
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo="Conteo del turno"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-conteo"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Registrando…' : 'Registrar conteo'}
                    </button>
                </>
            }
        >
            <form id="forma-conteo" className="columna g4" onSubmit={registrar}>
                <div className="aviso aviso-atencion">
                    Cuente el efectivo físico e imprima el cierre del lector <strong>antes</strong>{' '}
                    de continuar. Todo se registra una sola vez y no se puede corregir; recién
                    entonces el sistema muestra lo esperado y las diferencias.
                </div>

                <div>
                    <label className="etiqueta" htmlFor="contado">
                        Efectivo contado
                    </label>
                    <input
                        id="contado"
                        className="campo campo-numero campo-grande"
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        placeholder="0"
                        value={contado}
                        onChange={(e) => setContado(e.target.value)}
                    />
                </div>

                {conTarjeta.length > 0 && (
                    <div className="columna g3">
                        <span className="etiqueta">Cierre del lector de tarjetas</span>
                        <p className="tenue diminuto" style={{ margin: 0 }}>
                            Imprima el cierre del lector y copie aquí el total de cada medio. Lo que
                            deje en blanco no se compara.
                        </p>

                        {conTarjeta.map((m) => (
                            <div key={m.medioPagoId}>
                                <label className="etiqueta" htmlFor={`declarado-${m.medioPagoId}`}>
                                    {m.nombre}
                                </label>
                                <input
                                    id={`declarado-${m.medioPagoId}`}
                                    className="campo campo-numero"
                                    type="number"
                                    min={0}
                                    step={1}
                                    inputMode="numeric"
                                    placeholder="No declarado"
                                    value={declarados[m.medioPagoId] ?? ''}
                                    onChange={(e) =>
                                        setDeclarados((actuales) => ({
                                            ...actuales,
                                            [m.medioPagoId]: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        ))}
                    </div>
                )}

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

function PasoCierre({
    sesion,
    alCerrada,
    alCerrar,
}: {
    sesion: SesionCaja
    alCerrada: (sesion: SesionCaja) => void
    alCerrar: () => void
}) {
    const desglose = usarCargar(() => apiCaja.esperado(sesion.sesionCajaId), [sesion.sesionCajaId])
    const [observacion, setObservacion] = useState(sesion.observacionCierre ?? '')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    const diferencia = sesion.diferencia ?? 0
    const cuadra = diferencia === 0

    async function cerrar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (!cuadra && observacion.trim().length === 0) {
            setError('Explique la diferencia antes de cerrar.')
            return
        }

        setEnviando(true)

        try {
            alCerrada(
                await apiCaja.cerrar(sesion.sesionCajaId, observacion.trim() || undefined),
            )
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo="Cerrar caja"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-cierre"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Cerrando…' : 'Cerrar caja'}
                    </button>
                </>
            }
        >
            <form id="forma-cierre" className="columna g4" onSubmit={cerrar}>
                <div className="rejilla-datos">
                    <Dato titulo="Contado" valor={dinero(sesion.efectivoContado)} />
                    <Dato titulo="Esperado" valor={dinero(sesion.efectivoEsperado)} />
                </div>

                <div
                    className={`aviso ${
                        cuadra ? 'aviso-ok' : diferencia > 0 ? 'aviso-atencion' : 'aviso-error'
                    } fila entre g3`}
                >
                    <span>{cuadra ? 'La caja cuadra' : diferencia > 0 ? 'Sobrante' : 'Faltante'}</span>
                    <strong className="numero">{dinero(Math.abs(diferencia))}</strong>
                </div>

                <Estado
                    cargando={desglose.cargando}
                    error={desglose.error}
                    alReintentar={desglose.recargar}
                >
                    {desglose.datos && <Desglose desglose={desglose.datos} />}
                </Estado>

                <div>
                    <label className="etiqueta" htmlFor="observacion-cierre">
                        Observación {cuadra && <span className="tenue">(opcional)</span>}
                    </label>
                    <textarea
                        id="observacion-cierre"
                        className="campo campo-area"
                        rows={3}
                        maxLength={500}
                        placeholder={
                            cuadra
                                ? 'Cualquier nota del turno'
                                : 'Explique de dónde viene la diferencia'
                        }
                        value={observacion}
                        onChange={(e) => setObservacion(e.target.value)}
                    />
                </div>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

/**
 * Detalle de como se llega al esperado. Se pliega en el dialogo de cierre, pero
 * se muestra abierto en el reporte: un `<details>` cerrado no sale en el papel.
 */
function Desglose({ desglose, plegable = true }: { desglose: DesgloseEfectivo; plegable?: boolean }) {
    const lineas: { texto: string; valor: number; resta?: boolean }[] = [
        { texto: 'Efectivo inicial', valor: desglose.montoInicial },
        { texto: 'Ventas en efectivo', valor: desglose.ventasEnEfectivo },
        { texto: 'Abonos de crédito en efectivo', valor: desglose.abonosCreditoEnEfectivo },
        { texto: 'Ingresos', valor: desglose.ingresos },
        { texto: 'Egresos', valor: desglose.egresos, resta: true },
        { texto: 'Retiros', valor: desglose.retiros, resta: true },
        { texto: 'Devoluciones pagadas en efectivo', valor: desglose.reembolsosEnEfectivo, resta: true },
    ]

    const lista = (
        <ul className="lista-desglose">
            {lineas.map((linea) => (
                <li key={linea.texto} className="fila entre g3">
                    <span className="tenue chico">{linea.texto}</span>
                    <span className={`numero chico ${linea.resta ? 'monto-resta' : ''}`}>
                        {linea.resta ? '−' : ''}
                        {dinero(linea.valor)}
                    </span>
                </li>
            ))}

            <li className="fila entre g3 total-desglose">
                <span className="negrita">Esperado</span>
                <span className="numero negrita">{dinero(desglose.esperado)}</span>
            </li>
        </ul>
    )

    if (!plegable) return <div className="desglose">{lista}</div>

    return (
        <details className="desglose">
            <summary>Cómo se calculó el esperado</summary>
            {lista}
        </details>
    )
}

/* -------------------------------------------------------------------------- */
/* Reporte de cierre                                                           */
/* -------------------------------------------------------------------------- */

function ReporteDeCierre({
    sesion,
    alTerminar,
}: {
    sesion: SesionCaja
    alTerminar: () => void
}) {
    const reporte = usarCargar(
        () => apiCaja.reporteCierre(sesion.sesionCajaId),
        [sesion.sesionCajaId],
    )

    const diferencia = sesion.diferencia ?? 0

    return (
        <div className="pagina-caja columna g4">
            <section className="panel columna g4 reporte-cierre">
                <header className="fila entre envuelve g3">
                    <div className="columna g1">
                        <h1>Cierre de {sesion.cajaNombre}</h1>
                        <p className="tenue chico">
                            {fechaHora(sesion.abiertaEn)} — {fechaHora(sesion.cerradaEn)}
                        </p>
                    </div>

                    <span
                        className={`insignia ${
                            diferencia === 0 ? 'insignia-ok' : 'insignia-atencion'
                        }`}
                    >
                        {diferencia === 0
                            ? 'Cuadrada'
                            : `${diferencia > 0 ? 'Sobrante' : 'Faltante'} ${dinero(
                                  Math.abs(diferencia),
                              )}`}
                    </span>
                </header>

                <Estado
                    cargando={reporte.cargando}
                    error={reporte.error}
                    alReintentar={reporte.recargar}
                >
                    {reporte.datos && (
                        <div className="columna g5">
                            <div className="rejilla-datos">
                                <Dato titulo="Ventas" valor={String(reporte.datos.cantidadVentas)} />
                                <Dato titulo="Total vendido" valor={dinero(reporte.datos.totalVendido)} />
                                <Dato titulo="Devuelto" valor={dinero(reporte.datos.totalDevuelto)} />
                                <Dato titulo="Contado" valor={dinero(sesion.efectivoContado)} />
                                <Dato titulo="Esperado" valor={dinero(sesion.efectivoEsperado)} />
                            </div>

                            <section className="columna g3">
                                <h2>Por medio de pago</h2>

                                {reporte.datos.totalesPorMedioPago.length === 0 ? (
                                    <p className="vacio">No hubo cobros en este turno.</p>
                                ) : (
                                    <>
                                        <div className="tabla-envoltura">
                                            <table className="tabla">
                                                <thead>
                                                    <tr>
                                                        <th>Medio</th>
                                                        <th className="derecha">Operaciones</th>
                                                        <th className="derecha">Registrado</th>
                                                        <th className="derecha">Según lector</th>
                                                        <th className="derecha">Diferencia</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reporte.datos.totalesPorMedioPago.map((t) => (
                                                        <tr key={t.medioPagoId}>
                                                            <td>{t.nombre}</td>
                                                            <td className="derecha numero">
                                                                {t.cantidadOperaciones}
                                                            </td>
                                                            <td className="derecha numero">
                                                                {dinero(t.importe)}
                                                            </td>
                                                            <td className="derecha numero tenue">
                                                                {t.importeDeclarado === null
                                                                    ? '—'
                                                                    : dinero(t.importeDeclarado)}
                                                            </td>
                                                            <td className="derecha numero">
                                                                {t.diferencia === null ? (
                                                                    <span className="tenue">—</span>
                                                                ) : (
                                                                    <span
                                                                        className={`insignia ${
                                                                            t.diferencia === 0
                                                                                ? 'insignia-ok'
                                                                                : 'insignia-atencion'
                                                                        }`}
                                                                    >
                                                                        {t.diferencia === 0
                                                                            ? 'cuadra'
                                                                            : dinero(t.diferencia)}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        <p className="tenue diminuto">
                                            «Según lector» es lo que el cajero copió del cierre que
                                            imprime la máquina de tarjetas, antes de ver lo
                                            registrado. Un guión significa que no lo declaró, que no
                                            es lo mismo que cero.
                                        </p>
                                    </>
                                )}
                            </section>

                            <section className="columna g3">
                                <h2>Descuentos</h2>

                                {reporte.datos.totalPromociones === 0 &&
                                reporte.datos.totalDescuentosManuales === 0 ? (
                                    <p className="vacio">No se descontó nada en este turno.</p>
                                ) : (
                                    <>
                                        <div className="rejilla-datos">
                                            <Dato
                                                titulo="Por promociones"
                                                valor={dinero(reporte.datos.totalPromociones)}
                                            />
                                            <Dato
                                                titulo="A mano"
                                                valor={dinero(
                                                    reporte.datos.totalDescuentosManuales,
                                                )}
                                            />
                                        </div>

                                        <p className="tenue diminuto">
                                            Las promociones las aplica el sistema solo; los
                                            descuentos a mano los puso una persona. La caja cuadra
                                            igual con cualquiera de los dos —el efectivo esperado
                                            sale de los pagos ya descontados—, así que estas dos
                                            cifras son el único lugar donde se ven.
                                        </p>
                                    </>
                                )}
                            </section>

                            <section className="columna g3">
                                <h2>Efectivo</h2>
                                <Desglose desglose={reporte.datos.efectivo} plegable={false} />
                            </section>

                            {reporte.datos.movimientos.length > 0 && (
                                <section className="columna g3">
                                    <h2>Movimientos</h2>
                                    <TablaMovimientos movimientos={reporte.datos.movimientos} />
                                </section>
                            )}

                            {sesion.observacionCierre && (
                                <section className="columna g1">
                                    <h2>Observación</h2>
                                    <p className="chico">{sesion.observacionCierre}</p>
                                </section>
                            )}
                        </div>
                    )}
                </Estado>

                <div className="fila envuelve g3 acciones-caja sin-impresion">
                    <button
                        type="button"
                        className="boton"
                        onClick={() => window.print()}
                        disabled={reporte.cargando || reporte.error !== null}
                    >
                        Imprimir
                    </button>
                    <button type="button" className="boton boton-primario" onClick={alTerminar}>
                        Listo
                    </button>
                </div>
            </section>
        </div>
    )
}
